import React from "react";
import ReactApexChart from "react-apexcharts";
import { buildChartData, getNiceMax } from "../utils/chartDataUtils";
import MemberBreakdownList from "./MemberBreakdownList";
import TrendBadge from "Components/common/TrendBadge";

const buildChartOptions = (labels, isSingleDay, { max, tickAmount }, metricLabel, isBar = false) => ({
  chart: {
    type: isBar ? "bar" : "area",
    toolbar: { show: false },
    animations: { enabled: false },
    selection: { enabled: false },
    zoom: { enabled: false }
  },
  ...(isBar
    ? { plotOptions: { bar: { borderRadius: 3, columnWidth: "60%" } } }
    : {
        stroke: { curve: "smooth", width: 2 },
        markers: {
          size: isSingleDay ? 5 : 3,
          colors: ["#3953fb"],
          strokeColors: "#fff",
          strokeWidth: 2,
          hover: { size: isSingleDay ? 6 : 4 }
        },
        fill: {
          type: "solid",
          opacity: 0.2
        }
      }),
  colors: ["#3953fb"],
  dataLabels: { enabled: false },
  xaxis: {
    categories: labels,
    labels: { show: isSingleDay, style: { fontSize: "11px" } },
    axisBorder: { show: false },
    axisTicks: { show: false },
    tooltip: { enabled: false }
  },
  yaxis: {
    min: 0,
    max,
    tickAmount,
    labels: { show: isSingleDay, style: { fontSize: "11px" } }
  },
  grid: {
    borderColor: "#e8ebee",
    strokeDashArray: 3,
    padding: { left: 0, right: 0, top: 0, bottom: isSingleDay ? 0 : -10 },
    yaxis: { lines: { show: true } },
    xaxis: { lines: { show: false } }
  },
  tooltip: {
    y: {
      title: { formatter: () => (metricLabel ? `${metricLabel}:` : "") },
      formatter: val => Math.round(val).toLocaleString()
    }
  }
});

/**
 * `label` and `size` exist so callers outside the dashboard grid can use this
 * widget directly. On the dashboard the label lives in WidgetCard and the size
 * is fixed, so both default to the previous behaviour.
 *
 * `chartData` is a pre-built [{label,value}] escape hatch for callers that have
 * no dateFilter to bucket by — the copilot chat, for one. Falls back to the
 * dashboard's own bucketing when absent.
 */
const CountWidget = ({ value, trend, series, chartData: chartDataProp, dateFilter, customRange, metricLabel, unit, memberBreakdown, liAccountBreakdown, breakdownLoading, liAccountBreakdownLoading, visualization, label, size }) => {
  const displayValue = value != null ? value.toLocaleString() : "—";
  const isKpiOnly = visualization === "KPI";
  const isBar = visualization === "BAR";
  const hasSeries = !isKpiOnly && Array.isArray(series) && series.length > 0;

  const chartData = chartDataProp
    ? isKpiOnly
      ? []
      : chartDataProp
    : hasSeries
      ? buildChartData(series, dateFilter, customRange)
      : [];
  const isSingleDay = chartData.length === 1;
  const labels = chartData.map(d => d.label);
  const values = chartData.map(d => d.value || 0);
  const apexSeries = chartData.length > 0 ? [{ name: "Count", data: values }] : [];
  const options = buildChartOptions(labels, isSingleDay, getNiceMax(Math.max(...values, 1)), metricLabel, isBar);

  // Nest the LinkedIn-account breakdown under the member breakdown whenever both are present (the admin
  // case: member breakdown + its "Split by LinkedIn account" sub-toggle both on). Non-admins only ever
  // get liAccountBreakdown rows (their own accounts, no member rows), so they still fall back to the
  // flat list below.
  const hasMemberRows = Array.isArray(memberBreakdown) && memberBreakdown.length > 0;
  const hasLiRows = Array.isArray(liAccountBreakdown) && liAccountBreakdown.length > 0;
  const nestLiUnderMember = hasMemberRows && hasLiRows;
  const liByMember = nestLiUnderMember
    ? liAccountBreakdown.reduce((map, r) => {
        const key = r.memberId || "unknown";
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(r);
        return map;
      }, new Map())
    : null;

  return (
    <>
      {label && <div className="widgetCardValueLabel">{label}</div>}
      <div className={`widgetCardValue${size ? ` widgetCardValue--${size}` : ""}`}>
        {displayValue}
        {unit && <span className="widgetCardValueUnit">{unit}</span>}
      </div>
      <TrendBadge trend={trend} />
      <MemberBreakdownList
        rows={memberBreakdown}
        loading={breakdownLoading}
        heading="By member"
        subRowsByMemberId={liByMember}
      />
      {!nestLiUnderMember && (
        <MemberBreakdownList rows={liAccountBreakdown} loading={liAccountBreakdownLoading} heading="By LinkedIn account" />
      )}
      {apexSeries.length > 0 && (
        <div className={`rateWidgetChartWrap${isSingleDay ? " singleDayChart" : ""}`}>
          <ReactApexChart
            options={options}
            series={apexSeries}
            type={isBar ? "bar" : "area"}
            height={140}
          />
        </div>
      )}
    </>
  );
};

export default CountWidget;
